import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { useDispatch } from 'react-redux';
import { createElement } from 'react';
import type { AppDispatch } from '@/store/store';
import { getAccessToken } from '@/utils/jwt';
import { jobProgressReceived, jobCompleted, jobFailed, jobRemoved } from '@/store/slices/jobsSlice';
import { connectorsApi } from '@/store/api/connectorsApi';
import { knowledgeApi } from '@/store/api/knowledgeApi';
import { microagentApi } from '@/store/api/microagentApi';
import { invalidateTagsForAction, invalidateAllRegistryTags } from '@/hooks/wsInvalidation';
import type { RawWsMessage, WsMessage, JobCompletedPayload, JobFailedPayload } from '@/types/ws';
import type { ConnectionStatus } from '@/types/ws';

/** Evict a terminal job from Redux after a delay so the UI shows it briefly. */
const JOB_EVICTION_DELAY_MS = 5 * 60 * 1000; // 5 minutes

function scheduleJobEviction(jobId: string, dispatch: AppDispatch): void {
  setTimeout(() => dispatch(jobRemoved(jobId)), JOB_EVICTION_DELAY_MS);
}

/** Shared logic for job_completed and job_failed — Redux slice update + worker-conditional invalidation. */
function handleTerminalJob<T extends JobCompletedPayload | JobFailedPayload>(
  dispatch: AppDispatch,
  payload: T,
  action: (p: T) => { payload: T; type: string },
): void {
  dispatch(action(payload));
  if (payload.worker === 'ConnectorSyncWorker') {
    dispatch(connectorsApi.util.invalidateTags(['Connector']));
  }
  if (payload.worker === 'DocumentIngestWorker') {
    dispatch(knowledgeApi.util.invalidateTags(['Document']));
  }
  scheduleJobEviction(payload.job_id, dispatch);
}

function noop() {
  /* default context value — never called in practice */
}

interface SymbioteSocketContextValue {
  send: (action: string, payload: Record<string, unknown>) => void;
  status: ConnectionStatus;
}

const SymbioteSocketContext = createContext<SymbioteSocketContextValue>({
  send: noop,
  status: 'disconnected',
});

export function useSymbioteSocket(): SymbioteSocketContextValue {
  return useContext(SymbioteSocketContext);
}

export function handleMessage(raw: RawWsMessage, dispatch: AppDispatch): void {
  // Cast is safe: the switch only handles known actions; unknown actions fall through to default.
  const msg = raw as unknown as WsMessage;
  switch (msg.action) {
    case 'job_progress':
      dispatch(jobProgressReceived(msg.payload));
      break;

    case 'job_completed':
      handleTerminalJob(dispatch, msg.payload, jobCompleted);
      break;

    case 'job_failed':
      handleTerminalJob(dispatch, msg.payload, jobFailed);
      break;

    case 'microagent_log':
      dispatch(microagentApi.util.invalidateTags([{ type: 'Logs', id: msg.payload.agent_id }]));
      break;

    default:
      break;
  }
  // Generic registry-based cache invalidation for all mapped actions
  invalidateTagsForAction(raw.action, dispatch);
}

export function SymbioteSocketProvider({ children }: { children: ReactNode }) {
  const dispatch = useDispatch<AppDispatch>();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const attemptRef = useRef(0);
  const closingIntentionally = useRef(false);
  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const connectRef = useRef<() => void>(noop);

  const connect = useCallback(() => {
    const token = getAccessToken();
    if (!token) return;

    // Token passed via query param because WebSocket API doesn't support custom headers.
    // The design doc suggests subprotocol (`bearer-${token}`), but the backend middleware
    // (SymbioteWebSocketMiddleware) already parses the query param approach. If migrating
    // to subprotocol, update both this line and the backend middleware simultaneously.
    const wsUrl = `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}/ws/symbiote?token=${encodeURIComponent(token)}`;
    setStatus(attemptRef.current === 0 ? 'connecting' : 'reconnecting');

    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      if (attemptRef.current > 0) {
        invalidateAllRegistryTags(dispatch);
      }
      attemptRef.current = 0;
      setStatus('connected');
    };

    ws.onmessage = (event: MessageEvent) => {
      let raw: RawWsMessage;
      try {
        raw = JSON.parse(event.data as string) as RawWsMessage;
      } catch (err) {
        console.error('[SymbioteSocket] Failed to parse WebSocket frame:', err, event.data);
        return;
      }
      handleMessage(raw, dispatch);
    };

    ws.onclose = () => {
      setStatus('disconnected');
      if (closingIntentionally.current) return;
      // Exponential backoff: 1s, 2s, 4s, 8s, 16s, max 30s
      const delay = Math.min(1000 * 2 ** attemptRef.current, 30_000);
      attemptRef.current += 1;
      reconnectTimer.current = setTimeout(() => {
        connectRef.current();
      }, delay);
    };

    wsRef.current = ws;
  }, [dispatch]);

  const send = useCallback((action: string, payload: Record<string, unknown>) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action, payload }));
    }
  }, []);

  useEffect(() => {
    connectRef.current = connect;
    connect();
    return () => {
      closingIntentionally.current = true;
      clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
  }, [connect]);

  const value = useMemo(() => ({ send, status }), [send, status]);

  // eslint-disable-next-line react-hooks/refs -- false positive: value is from useMemo, not a ref
  return createElement(SymbioteSocketContext.Provider, { value }, children);
}
